import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  StepDefinitionSchema,
  EdgeDefinitionSchema,
  WorkflowRunStatusSchema,
  StepRunStatusSchema,
  StepCategorySchema,
  SocketDefinitionSchema,
} from "@erp/workflows";

/**
 * Workflow definition creation request
 */
export const CreateWorkflowDefinitionRequestSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().optional(),
  steps: z.array(StepDefinitionSchema),
  edges: z.array(EdgeDefinitionSchema),
  tags: z.array(z.string()).optional(),
});

/**
 * Workflow definition update request
 */
export const UpdateWorkflowDefinitionRequestSchema = z.object({
  workflowDefinitionId: z.uuid(),
  name: z.string().min(1).max(255).optional(),
  description: z.string().optional(),
  steps: z.array(StepDefinitionSchema).optional(),
  edges: z.array(EdgeDefinitionSchema).optional(),
  tags: z.array(z.string()).optional(),
  isActive: z.boolean().optional(),
});

/**
 * Workflow definition duplicate request
 */
export const DuplicateWorkflowDefinitionRequestSchema = z.object({
  workflowDefinitionId: z.string().uuid(),
  name: z.string().min(1).max(255),
});

/**
 * Workflow definition response
 */
export const WorkflowDefinitionResponseSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().optional(),
  version: z.number(),
  isActive: z.boolean(),
  organizationId: z.uuid(),
  steps: z.array(StepDefinitionSchema),
  edges: z.array(EdgeDefinitionSchema),
  createdBy: z.uuid(),
  tags: z.array(z.string()),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

/**
 * Trigger workflow request
 */
export const TriggerWorkflowRequestSchema = z.object({
  workflowDefinitionId: z.uuid(),
  initialInputs: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Workflow run response
 */
export const WorkflowRunResponseSchema = z.object({
  id: z.uuid(),
  workflowDefinitionId: z.uuid(),
  status: WorkflowRunStatusSchema,
  triggeredBy: z.uuid().nullable(), // null for system-triggered workflows
  triggeredAt: z.iso.datetime(),
  startedAt: z.iso.datetime().optional(),
  completedAt: z.iso.datetime().optional(),
  stopReason: z.string().optional(),
  errorStepId: z.uuid().optional(),
  totalSteps: z.number(),
  completedSteps: z.number(),
  sentryTraceId: z.string().optional(),
  // Parent-child workflow support
  parentWorkflowRunId: z.uuid().optional(),
  parentContext: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Get workflow run request
 */
export const GetWorkflowRunRequestSchema = z.object({
  workflowRunId: z.uuid(),
});

/**
 * Step run response
 */
export const StepRunResponseSchema = z.object({
  id: z.uuid(),
  workflowRunId: z.uuid(),
  stepDefinitionId: z.uuid(),
  attemptNumber: z.number(),
  status: StepRunStatusSchema,
  inputData: z.record(z.string(), z.unknown()),
  outputData: z.record(z.string(), z.unknown()).optional(),
  queuedAt: z.iso.datetime().optional(),
  startedAt: z.iso.datetime().optional(),
  completedAt: z.iso.datetime().optional(),
  duration: z.number().optional(),
  stopReason: z.string().optional(),
  sentrySpanId: z.string().optional(),
});

/**
 * List available step types response
 */
export const ListStepTypesResponseSchema = z.object({
  stepTypes: z.array(
    z.object({
      type: z.string(),
      name: z.string(),
      description: z.string(),
      category: StepCategorySchema,
      inputSockets: z.array(SocketDefinitionSchema),
      outputSockets: z.array(SocketDefinitionSchema),
      // JSON Schema representation of the step's config schema
      configSchema: z.record(z.string(), z.unknown()).optional(),
    }),
  ),
});

/**
 * Socket definition for templates (uses permissive string type)
 */
export const TemplateSocketDefinitionSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(), // More permissive than SocketDefinitionSchema
  required: z.boolean(),
  description: z.string().optional(),
});

/**
 * Workflow template step schema (simplified from StepDefinitionSchema)
 */
export const TemplateStepSchema = z.object({
  stepType: z.string(),
  name: z.string(),
  category: z.string(),
  config: z.record(z.string(), z.unknown()),
  position: z.object({ x: z.number(), y: z.number() }),
  sockets: z.object({
    inputs: z.array(TemplateSocketDefinitionSchema),
    outputs: z.array(TemplateSocketDefinitionSchema),
  }),
  componentWorkflowId: z.uuid().optional(),
  emits: z.array(z.string()).optional(),
});

/**
 * Workflow template edge schema
 */
export const TemplateEdgeSchema = z.object({
  sourceStepIndex: z.number(),
  sourceSocketId: z.string(),
  targetStepIndex: z.number(),
  targetSocketId: z.string(),
});

/**
 * Workflow template response - full template structure
 */
export const WorkflowTemplateResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  version: z.number(),
  isActive: z.boolean(),
  tags: z.array(z.string()),
  isComponent: z.boolean(),
  steps: z.array(TemplateStepSchema),
  edges: z.array(TemplateEdgeSchema),
  retryConfig: z
    .object({
      enabled: z.boolean(),
      maxRetries: z.number(),
      initialDelay: z.number(),
      maxDelay: z.number(),
      backoffMultiplier: z.number(),
    })
    .optional(),
  exposedInputs: z.array(z.string()).optional(),
  exposedOutputs: z.array(z.string()).optional(),
  category: z.enum(["email", "ap", "po", "integration", "custom"]).optional(),
});

/**
 * List workflow templates response
 */
export const ListWorkflowTemplatesResponseSchema = z.object({
  templates: z.array(WorkflowTemplateResponseSchema),
});

/**
 * Instantiate workflow template request
 */
export const InstantiateWorkflowTemplateRequestSchema = z.object({
  aiCreationId: z.uuid(),
});

/**
 * Workflow definition summary - lightweight version for listing
 */
export const WorkflowDefinitionSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().optional(),
  version: z.number(),
  isActive: z.boolean(),
  tags: z.array(z.string()),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  nodeCount: z.number(),
  lastRun: WorkflowRunResponseSchema.optional(),
});

/**
 * List workflow definition summaries response
 */
export const ListWorkflowDefinitionSummariesResponseSchema = z.object({
  workflows: z.array(WorkflowDefinitionSummarySchema),
  total: z.number(),
});

/**
 * List runs for a workflow definition response
 */
export const ListWorkflowRunsForDefinitionResponseSchema = z.object({
  runs: z.array(WorkflowRunResponseSchema),
  total: z.number(),
});

/**
 * Retry step request - retry a failed/timed_out step with optional input override
 */
export const RetryStepRequestSchema = z.object({
  workflowRunId: z.uuid(),
  stepRunId: z.uuid(),
  inputData: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Retry step response
 */
export const RetryStepResponseSchema = z.object({
  stepRun: StepRunResponseSchema,
  workflowRun: WorkflowRunResponseSchema,
});

/**
 * Restart from step request - restart workflow from a specific step
 */
export const RestartFromStepRequestSchema = z.object({
  workflowRunId: z.uuid(),
  workflowStepId: z.uuid(),
  inputData: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Restart from step response
 */
export const RestartFromStepResponseSchema = z.object({
  stepRun: StepRunResponseSchema,
  workflowRun: WorkflowRunResponseSchema,
  cancelledStepRunIds: z.array(z.uuid()),
});

/**
 * Workflow contracts
 */
export const workflowContracts = {
  // Create a workflow definition
  createDefinition: oc
    .input(CreateWorkflowDefinitionRequestSchema)
    .output(WorkflowDefinitionResponseSchema)
    .route({ method: "POST" }),

  // Update a workflow definition
  updateDefinition: oc
    .input(UpdateWorkflowDefinitionRequestSchema)
    .output(WorkflowDefinitionResponseSchema)
    .route({ method: "PUT" }),

  // Get a workflow definition by ID
  getDefinition: oc
    .input(z.object({ workflowDefinitionId: z.uuid() }))
    .output(WorkflowDefinitionResponseSchema)
    .route({ method: "GET" }),

  // List workflow definitions
  listDefinitions: oc
    .input(
      z.object({
        limit: z.number().optional(),
        offset: z.number().optional(),
      }),
    )
    .output(
      z.object({
        workflows: z.array(WorkflowDefinitionResponseSchema),
        total: z.number(),
      }),
    )
    .route({ method: "GET" }),

  // Trigger a workflow
  trigger: oc
    .input(TriggerWorkflowRequestSchema)
    .output(WorkflowRunResponseSchema)
    .route({ method: "POST" }),

  // Get workflow run status
  getRun: oc
    .input(GetWorkflowRunRequestSchema)
    .output(WorkflowRunResponseSchema)
    .route({ method: "GET" }),

  // Get step runs for a workflow run
  getStepRuns: oc
    .input(z.object({ workflowRunId: z.uuid() }))
    .output(
      z.object({
        stepRuns: z.array(StepRunResponseSchema),
      }),
    )
    .route({ method: "GET" }),

  // List available step types
  listStepTypes: oc
    .input(z.object({}))
    .output(ListStepTypesResponseSchema)
    .route({ method: "GET" }),

  // List available workflow templates
  listTemplates: oc
    .input(z.object({}))
    .output(ListWorkflowTemplatesResponseSchema)
    .route({ method: "GET" }),

  // Instantiate a workflow template
  instantiateTemplate: oc
    .input(InstantiateWorkflowTemplateRequestSchema)
    .output(WorkflowDefinitionResponseSchema)
    .route({ method: "POST" }),

  // List workflow definition summaries (with node count and last run)
  listDefinitionSummaries: oc
    .input(
      z.object({
        limit: z.number().optional(),
        offset: z.number().optional(),
      }),
    )
    .output(ListWorkflowDefinitionSummariesResponseSchema)
    .route({ method: "GET" }),

  // List runs for a workflow definition
  listRunsForDefinition: oc
    .input(
      z.object({
        workflowDefinitionId: z.uuid(),
        limit: z.number().optional(),
        offset: z.number().optional(),
      }),
    )
    .output(ListWorkflowRunsForDefinitionResponseSchema)
    .route({ method: "GET" }),

  // Retry a failed or timed_out step
  retryStep: oc
    .input(RetryStepRequestSchema)
    .output(RetryStepResponseSchema)
    .route({ method: "POST" }),

  // Restart workflow from a specific step (cancels downstream steps)
  restartFromStep: oc
    .input(RestartFromStepRequestSchema)
    .output(RestartFromStepResponseSchema)
    .route({ method: "POST" }),

  // Get child workflow runs for a parent workflow
  getChildWorkflowRuns: oc
    .input(z.object({ parentWorkflowRunId: z.uuid() }))
    .output(
      z.object({
        childWorkflowRuns: z.array(WorkflowRunResponseSchema),
      }),
    )
    .route({ method: "GET" }),

  // Duplicate a workflow definition
  duplicateDefinition: oc
    .input(DuplicateWorkflowDefinitionRequestSchema)
    .output(WorkflowDefinitionResponseSchema)
    .route({ method: "POST" }),
} as const;

/**
 * Export types
 */
export type CreateWorkflowDefinitionRequest = z.infer<
  typeof CreateWorkflowDefinitionRequestSchema
>;
export type UpdateWorkflowDefinitionRequest = z.infer<
  typeof UpdateWorkflowDefinitionRequestSchema
>;
export type WorkflowDefinitionResponse = z.infer<
  typeof WorkflowDefinitionResponseSchema
>;
export type TriggerWorkflowRequest = z.infer<
  typeof TriggerWorkflowRequestSchema
>;
export type WorkflowRunResponse = z.infer<typeof WorkflowRunResponseSchema>;
export type GetWorkflowRunRequest = z.infer<typeof GetWorkflowRunRequestSchema>;
export type StepRunResponse = z.infer<typeof StepRunResponseSchema>;
export type ListStepTypesResponse = z.infer<typeof ListStepTypesResponseSchema>;
export type WorkflowTemplateResponse = z.infer<
  typeof WorkflowTemplateResponseSchema
>;
export type ListWorkflowTemplatesResponse = z.infer<
  typeof ListWorkflowTemplatesResponseSchema
>;
export type InstantiateWorkflowTemplateRequest = z.infer<
  typeof InstantiateWorkflowTemplateRequestSchema
>;
export type WorkflowDefinitionSummary = z.infer<
  typeof WorkflowDefinitionSummarySchema
>;
export type ListWorkflowDefinitionSummariesResponse = z.infer<
  typeof ListWorkflowDefinitionSummariesResponseSchema
>;
export type ListWorkflowRunsForDefinitionResponse = z.infer<
  typeof ListWorkflowRunsForDefinitionResponseSchema
>;
export type RetryStepRequest = z.infer<typeof RetryStepRequestSchema>;
export type RetryStepResponse = z.infer<typeof RetryStepResponseSchema>;
export type RestartFromStepRequest = z.infer<
  typeof RestartFromStepRequestSchema
>;
export type RestartFromStepResponse = z.infer<
  typeof RestartFromStepResponseSchema
>;
export type DuplicateWorkflowDefinitionRequest = z.infer<
  typeof DuplicateWorkflowDefinitionRequestSchema
>;
